import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, Loader2, KeyRound, ShieldCheck } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getPasswordStrength(pw) {
  if (!pw) return { score: 0, label: '', color: '' };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 2) return { score: Math.round(score / 6 * 100), label: 'Weak', color: 'bg-red-500' };
  if (score <= 3) return { score: Math.round(score / 6 * 100), label: 'Fair', color: 'bg-amber-500' };
  if (score <= 4) return { score: Math.round(score / 6 * 100), label: 'Good', color: 'bg-blue-500' };
  return { score: Math.round(score / 6 * 100), label: 'Strong', color: 'bg-green-500' };
}

function isPasswordValid(pw) {
  return pw && pw.length >= 8 && /[A-Z]/.test(pw) && /[a-z]/.test(pw) && /[0-9]/.test(pw);
}

export default function LoginPage() {
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');

  // First-time setup
  const [needsSetup, setNeedsSetup] = useState(null); // null=loading, true/false
  const [setupForm, setSetupForm] = useState({ name: '', email: '', password: '' });
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupError, setSetupError] = useState('');
  const [showSetupPw, setShowSetupPw] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/setup-status`).then(r => setNeedsSetup(r.data.needs_setup)).catch(() => setNeedsSetup(false));
  }, []);

  const handleSetup = async (e) => {
    e.preventDefault();
    if (!setupForm.name || !setupForm.email || !setupForm.password) { setSetupError('All fields required'); return; }
    if (!isPasswordValid(setupForm.password)) { setSetupError('Password must be 8+ chars with uppercase, lowercase, and digit'); return; }
    setSetupLoading(true); setSetupError('');
    try {
      const res = await axios.post(`${API}/auth/bootstrap`, setupForm);
      localStorage.setItem('keya_token', res.data.token);
      toast.success('Admin account created! Redirecting...');
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      setSetupError(err.response?.data?.detail || 'Setup failed');
    } finally { setSetupLoading(false); }
  };

  const setupPwStrength = getPasswordStrength(setupForm.password);

  // Forgot password states
  const [fpStep, setFpStep] = useState(0); // 0=login, 1=enter email, 2=enter OTP, 3=new password
  const [fpEmail, setFpEmail] = useState('');
  const [fpOtp, setFpOtp] = useState('');
  const [fpResetToken, setFpResetToken] = useState('');
  const [fpNewPw, setFpNewPw] = useState('');
  const [fpLoading, setFpLoading] = useState(false);
  const [fpError, setFpError] = useState('');
  const [fpSuccess, setFpSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(form.email, form.password);
      toast.success('Welcome back!');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Authentication failed';
      setError(msg);
      if (err.response?.status === 429 || err.response?.status === 423) {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!fpEmail) return;
    setFpLoading(true); setFpError('');
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: fpEmail });
      setFpStep(2);
      setFpSuccess('OTP sent to your email. Check your inbox.');
    } catch (err) {
      setFpError(err.response?.data?.detail || 'Failed to send OTP. Check SMTP configuration.');
    } finally { setFpLoading(false); }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!fpOtp) return;
    setFpLoading(true); setFpError(''); setFpSuccess('');
    try {
      const res = await axios.post(`${API}/auth/verify-otp`, { email: fpEmail, otp: fpOtp });
      setFpResetToken(res.data.reset_token);
      setFpStep(3);
      setFpSuccess('OTP verified. Set your new password.');
    } catch (err) {
      setFpError(err.response?.data?.detail || 'Invalid OTP');
    } finally { setFpLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!isPasswordValid(fpNewPw)) { setFpError('Password too weak'); return; }
    setFpLoading(true); setFpError('');
    try {
      await axios.post(`${API}/auth/reset-password`, { reset_token: fpResetToken, password: fpNewPw });
      setFpSuccess('Password reset successful!');
      toast.success('Password reset. You can now log in.');
      setTimeout(() => { setFpStep(0); setFpEmail(''); setFpOtp(''); setFpNewPw(''); setFpResetToken(''); setFpSuccess(''); }, 1500);
    } catch (err) {
      setFpError(err.response?.data?.detail || 'Failed to reset password');
    } finally { setFpLoading(false); }
  };

  const resetForgotFlow = () => {
    setFpStep(0); setFpEmail(''); setFpOtp(''); setFpNewPw(''); setFpResetToken(''); setFpError(''); setFpSuccess('');
  };

  const pwStrength = getPasswordStrength(fpNewPw);

  // Loading setup status
  if (needsSetup === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <img src="/keya-logo.png" alt="Keya" className="w-16 h-16 object-contain animate-pulse" />
      </div>
    );
  }

  // First-Time Setup
  if (needsSetup) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="setup-page">
        <div className="w-full max-w-sm bg-card border border-border rounded-lg shadow-sm p-8 space-y-6">
          <div className="space-y-2">
            <div className="flex items-center gap-3 mb-4">
              <img src="/keya-logo.png" alt="Keya" className="w-12 h-12 object-contain rounded" />
              <div>
                <h1 className="font-heading font-bold text-xl tracking-tight">KEYA PULSE <span className="text-xs font-normal text-muted-foreground">v1.0</span></h1>
                <p className="text-xs text-muted-foreground tracking-widest uppercase">Sales Analytics</p>
              </div>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck size={20} className="text-primary" />
              <h2 className="font-heading font-semibold text-xl tracking-tight">Initial Setup</h2>
            </div>
            <p className="text-sm text-muted-foreground">Create the first admin account to get started. This is a one-time setup.</p>
          </div>

          <form onSubmit={handleSetup} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Full Name</Label>
              <Input placeholder="Admin Name" value={setupForm.name} onChange={e => setSetupForm({...setupForm, name: e.target.value})} required data-testid="setup-name" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Email</Label>
              <Input type="email" placeholder="admin@company.com" value={setupForm.email} onChange={e => setSetupForm({...setupForm, email: e.target.value})} required data-testid="setup-email" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Password</Label>
              <div className="relative">
                <Input type={showSetupPw ? 'text' : 'password'} placeholder="Create strong password" value={setupForm.password}
                  onChange={e => setSetupForm({...setupForm, password: e.target.value})} required data-testid="setup-password" />
                <button type="button" onClick={() => setShowSetupPw(!showSetupPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {showSetupPw ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              {setupForm.password && (
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className={`h-full ${setupPwStrength.color} transition-all duration-300`} style={{ width: `${setupPwStrength.score}%` }} />
                    </div>
                    <span className={`text-xs font-medium ${setupPwStrength.score <= 33 ? 'text-red-500' : setupPwStrength.score <= 66 ? 'text-amber-500' : 'text-green-500'}`}>
                      {setupPwStrength.label}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground">
                    <span className={setupForm.password.length >= 8 ? 'text-green-600 dark:text-green-400' : ''}>8+ chars {setupForm.password.length >= 8 ? '\u2713' : ''}</span>
                    <span className={/[A-Z]/.test(setupForm.password) ? 'text-green-600 dark:text-green-400' : ''}>Uppercase {/[A-Z]/.test(setupForm.password) ? '\u2713' : ''}</span>
                    <span className={/[a-z]/.test(setupForm.password) ? 'text-green-600 dark:text-green-400' : ''}>Lowercase {/[a-z]/.test(setupForm.password) ? '\u2713' : ''}</span>
                    <span className={/[0-9]/.test(setupForm.password) ? 'text-green-600 dark:text-green-400' : ''}>Digit {/[0-9]/.test(setupForm.password) ? '\u2713' : ''}</span>
                  </div>
                </div>
              )}
            </div>
            {setupError && <p className="text-sm text-destructive font-medium" data-testid="setup-error">{setupError}</p>}
            <Button type="submit" className="w-full h-11 font-heading font-semibold" disabled={setupLoading || !isPasswordValid(setupForm.password)} data-testid="setup-submit">
              {setupLoading ? <Loader2 size={14} className="mr-2 animate-spin" /> : <ShieldCheck size={14} className="mr-2" />}
              Create Admin Account
            </Button>
          </form>

          <p className="text-[11px] text-muted-foreground text-center">This account will have full admin access. Additional users can be created from the Users page after login.</p>
        </div>
      </div>
    );
  }

  // Forgot Password Flow
  if (fpStep > 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="forgot-password-page">
        <div className="w-full max-w-sm bg-card border border-border rounded-lg shadow-sm p-8 space-y-6">
          <div className="space-y-2">
            <button onClick={resetForgotFlow} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="back-to-login">
              <ArrowLeft size={14} /> Back to login
            </button>
            <div className="flex items-center gap-3 mb-4">
              <img src="/keya-logo.png" alt="Keya" className="w-10 h-10 object-contain rounded" />
              <span className="font-heading font-bold text-lg tracking-tight">KEYA PULSE <span className="text-xs font-normal text-muted-foreground">v1.0</span></span>
            </div>
            <h2 className="font-heading font-semibold text-xl tracking-tight">
              {fpStep === 1 && 'Forgot Password'}
              {fpStep === 2 && 'Enter OTP'}
              {fpStep === 3 && 'Set New Password'}
            </h2>
            <p className="text-sm text-muted-foreground">
              {fpStep === 1 && 'Enter your email to receive a one-time password'}
              {fpStep === 2 && `A 6-digit OTP was sent to ${fpEmail}`}
              {fpStep === 3 && 'Create a strong new password'}
            </p>
          </div>

          {/* Step indicators */}
          <div className="flex gap-1">
            {[1, 2, 3].map(s => (
              <div key={s} className={`h-1 flex-1 rounded-full ${s <= fpStep ? 'bg-primary' : 'bg-muted'}`} />
            ))}
          </div>

          {fpError && <p className="text-sm text-destructive font-medium" data-testid="fp-error">{fpError}</p>}
          {fpSuccess && <p className="text-sm text-green-600 dark:text-green-400 font-medium" data-testid="fp-success">{fpSuccess}</p>}

          {/* Step 1: Email */}
          {fpStep === 1 && (
            <form onSubmit={handleSendOTP} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Email</Label>
                <Input type="email" placeholder="you@company.com" value={fpEmail} onChange={e => setFpEmail(e.target.value)}
                  required data-testid="fp-email-input" />
              </div>
              <Button type="submit" className="w-full h-11 font-heading font-semibold" disabled={fpLoading} data-testid="fp-send-otp-btn">
                {fpLoading ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
                Send OTP
              </Button>
            </form>
          )}

          {/* Step 2: OTP */}
          {fpStep === 2 && (
            <form onSubmit={handleVerifyOTP} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">6-Digit OTP</Label>
                <Input type="text" placeholder="123456" value={fpOtp} onChange={e => setFpOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6} className="text-center text-xl tracking-[0.5em] font-mono font-bold" required data-testid="fp-otp-input" />
                <p className="text-[11px] text-muted-foreground">Expires in 10 minutes. Check spam folder if not received.</p>
              </div>
              <Button type="submit" className="w-full h-11 font-heading font-semibold" disabled={fpLoading || fpOtp.length !== 6} data-testid="fp-verify-otp-btn">
                {fpLoading ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
                Verify OTP
              </Button>
              <button type="button" onClick={() => { setFpStep(1); setFpOtp(''); setFpError(''); setFpSuccess(''); }}
                className="w-full text-sm text-muted-foreground hover:text-foreground text-center" data-testid="fp-resend-link">
                Didn't receive? Send again
              </button>
            </form>
          )}

          {/* Step 3: New Password */}
          {fpStep === 3 && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">New Password</Label>
                <div className="relative">
                  <Input type={showPw ? 'text' : 'password'} placeholder="Create strong password" value={fpNewPw}
                    onChange={e => setFpNewPw(e.target.value)} required data-testid="fp-new-password-input" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                {fpNewPw && (
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className={`h-full ${pwStrength.color} transition-all duration-300`} style={{ width: `${pwStrength.score}%` }} />
                      </div>
                      <span className={`text-xs font-medium ${pwStrength.score <= 33 ? 'text-red-500' : pwStrength.score <= 66 ? 'text-amber-500' : 'text-green-500'}`}>
                        {pwStrength.label}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground">
                      <span className={fpNewPw.length >= 8 ? 'text-green-600 dark:text-green-400' : ''}>8+ chars {fpNewPw.length >= 8 ? '\u2713' : ''}</span>
                      <span className={/[A-Z]/.test(fpNewPw) ? 'text-green-600 dark:text-green-400' : ''}>Uppercase {/[A-Z]/.test(fpNewPw) ? '\u2713' : ''}</span>
                      <span className={/[a-z]/.test(fpNewPw) ? 'text-green-600 dark:text-green-400' : ''}>Lowercase {/[a-z]/.test(fpNewPw) ? '\u2713' : ''}</span>
                      <span className={/[0-9]/.test(fpNewPw) ? 'text-green-600 dark:text-green-400' : ''}>Digit {/[0-9]/.test(fpNewPw) ? '\u2713' : ''}</span>
                    </div>
                  </div>
                )}
              </div>
              <Button type="submit" className="w-full h-11 font-heading font-semibold" disabled={fpLoading || !isPasswordValid(fpNewPw)} data-testid="fp-reset-btn">
                {fpLoading ? <Loader2 size={14} className="mr-2 animate-spin" /> : <KeyRound size={14} className="mr-2" />}
                Reset Password
              </Button>
            </form>
          )}
        </div>
      </div>
    );
  }

  // Login Form
  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="login-page">
      <div className="w-full max-w-sm bg-card border border-border rounded-lg shadow-sm p-8 space-y-8">
        <div className="space-y-2">
          <div className="flex items-center gap-3 mb-8">
            <img src="/keya-logo.png" alt="Keya" className="w-12 h-12 object-contain rounded" />
            <div>
              <h1 className="font-heading font-bold text-xl tracking-tight">KEYA PULSE <span className="text-xs font-normal text-muted-foreground">v1.0</span></h1>
              <p className="text-xs text-muted-foreground tracking-widest uppercase">Sales Analytics</p>
            </div>
          </div>
          <h2 className="font-heading font-semibold text-2xl tracking-tight">Welcome back</h2>
          <p className="text-sm text-muted-foreground">Sign in to your analytics dashboard</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Email</Label>
            <Input id="email" data-testid="login-email-input" type="email" placeholder="you@company.com" value={form.email}
              onChange={e => setForm({...form, email: e.target.value})} required />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Password</Label>
              <button type="button" onClick={() => setFpStep(1)} className="text-xs text-primary hover:underline font-medium" data-testid="forgot-password-link">
                Forgot password?
              </button>
            </div>
            <div className="relative">
              <Input id="password" data-testid="login-password-input" type={showPw ? 'text' : 'password'} placeholder="Enter password" value={form.password}
                onChange={e => setForm({...form, password: e.target.value})} required />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          {error && (
            <p className="text-sm text-destructive font-medium" data-testid="login-error">{error}</p>
          )}
          <Button type="submit" data-testid="login-submit-btn" className="w-full h-11 font-heading font-semibold" disabled={loading}>
            {loading ? 'Please wait...' : 'Sign In'}
          </Button>
        </form>
      </div>
    </div>
  );
}
